import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BaseKeyword, Keyword, SourceType } from '../../interfaces';

const baseKeywordsOptions = require('../../data-keys.json');
/** baseKeywordsOptions
  "keyword": the main control flag; this is the name will be returned to BE
  "alias": the name displayed to the user - can be overwritten when is instantiated
  "source": if this is SourceType.entity that means we will pull data from cache, otherwise no
  "sourceKey": internal main key from data cache system
  "sourceNode": this is used in some cases for nodes as children with data
*/

@Injectable({
  providedIn: 'root'
})
/**
 * Service used to parse keywords
 */
export class AdvancedSearchKeywordsService {
  /**
   * Prepare keywords for the autocomplete at init to be used later
   * @param {Keyword[]} allowedKeywords - The keywords list that are allowed to be used
   */
  public prepareAutoCompleteKeywords(allowedKeywords: Keyword[]): BaseKeyword[] {
    if (allowedKeywords && allowedKeywords.length) {
      const keywordsOptions = [];
      baseKeywordsOptions.forEach(keywordElement => {
        const allowedKey = allowedKeywords.find(el => el.keyword === keywordElement.keyword) as Keyword;

        if (allowedKey) {
          const newKeywordElement = cloneDeep(keywordElement);

          if (allowedKey.alias && keywordElement.alias !== allowedKey.alias) {
            newKeywordElement.alias = allowedKey.alias;
          }

          newKeywordElement.value = newKeywordElement.alias ? newKeywordElement.alias : newKeywordElement.keyword;

          keywordsOptions.push(newKeywordElement);
        }
      });

      return keywordsOptions;
    }

    return baseKeywordsOptions.map(keywordElement => {
      const newKeywordElement = cloneDeep(keywordElement);
      newKeywordElement.value = newKeywordElement ? newKeywordElement.alias : newKeywordElement.keyword;
      return newKeywordElement;
    });
  }

  /**
   * Get a list with all existing keywords
   */
  public getAllExistingKeywords(): Keyword[] {
    return cloneDeep(baseKeywordsOptions);
  }

  /**
   * Filter keywords by a source type
   * @param {Keyword[]} keywords - Keywords object
   * @param {SourceType} sourceType - Source type need it for filtering
   */
  public filterKeywordsBySource(keywords: Keyword[], sourceType: SourceType): BaseKeyword[] {
    const allowedFilters = keywords && keywords.map(keyword => keyword.keyword);
    return baseKeywordsOptions.filter(keyword => keyword.source === sourceType && allowedFilters.includes(keyword.keyword));
  }

  /**
   * Enrich keywords and drops the unknown ones.
   * @param {string[]} keywords - Keywords that needs to be enriched
   */
  public enrichKeywordsByKeyword(keywords: string[]): BaseKeyword[] {
    return keywords.map(keyword => baseKeywordsOptions.find(baseKeyword => baseKeyword.keyword === keyword)).filter(keyword => keyword !== undefined);
  }

  /**
   * Event called at on group chips
   * @param {BaseKeyword[]} keywordsOptions - The list of keywords
   * @param {string} keyword - the keyword that needs the value from autocomplete Options list
   */
  public getKeywordAlias(keywordsOptions: BaseKeyword[], keyword: string): string {
    const ret = keywordsOptions.find(el => el.value?.toLowerCase() === keyword.trim().toLowerCase());
    return ret ? ret.value : undefined;
  }

  /**
   * Checks if a keyword is allowed to be used
   * @param {BaseKeyword[]} keywordsOptions - The list of keywords
   * @param {string} keyword - the keyword that needs
   */
  public isKeywordAllowed(keywordsOptions: BaseKeyword[], keyword: string): boolean {
    return keywordsOptions.some(el => el.value?.toLowerCase() === keyword.trim().toLowerCase());
  }

  /**
   * Extract all available details for a keyword
   * @param {BaseKeyword[]} keywordsOptions - The list of keywords
   * @param {string} keyword - the keyword that needs to be enriched
   */
  public getKeywordDetailsByAlias(keywordsOptions: BaseKeyword[], keyword: string): BaseKeyword {
    return keywordsOptions.find(el => el.value?.toLowerCase() === keyword.toLowerCase());
  }

  /**
   * Get a keyword based on source key, keyword or alias
   * @param {string} keyword - the keyword equivalent for the desired keyword
   */
  public getKeywordByAny(keyword: string): BaseKeyword {
    return baseKeywordsOptions.find(el => el.sourceKey === keyword || el.keyword === keyword || el.alias === keyword);
  }
}
